/**
 * useNativeShare - Hook for sharing to native installed apps
 * 
 * Priority:
 * 1. Native URI schemes (opens installed app directly)
 * 2. Web Share API (system share sheet)
 * 3. Browser URL fallback
 */

const PLATFORMS = {
  whatsapp: {
    name: 'WhatsApp',
    nativeScheme: (text) => `whatsapp://send?text=${encodeURIComponent(text)}`,
    webFallback: (text) => `https://wa.me/?text=${encodeURIComponent(text)}`,
  },
  instagram: {
    name: 'Instagram',
    nativeScheme: () => `instagram://app`,
    webFallback: () => `https://www.instagram.com/`,
  },
  tiktok: {
    name: 'TikTok',
    nativeScheme: () => `tiktok://`,
    webFallback: () => `https://www.tiktok.com/upload`,
  },
  twitter: {
    name: 'X',
    nativeScheme: (text, url) => `twitter://post?message=${encodeURIComponent(text + (url ? ' ' + url : ''))}`,
    webFallback: (text, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}${url ? '&url=' + encodeURIComponent(url) : ''}`,
  },
  facebook: {
    name: 'Facebook',
    nativeScheme: (text, url) => url ? `fb://share?link=${encodeURIComponent(url)}` : `fb://`,
    webFallback: (text, url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url || '')}${text ? '&quote=' + encodeURIComponent(text) : ''}`,
  },
  telegram: {
    name: 'Telegram',
    nativeScheme: (text, url) => `tg://msg?text=${encodeURIComponent(text + (url ? ' ' + url : ''))}`,
    webFallback: (text, url) => `https://t.me/share/url?url=${encodeURIComponent(url || '')}&text=${encodeURIComponent(text)}`,
  },
  snapchat: {
    name: 'Snapchat',
    nativeScheme: () => `snapchat://`,
    webFallback: () => `https://www.snapchat.com/`,
  },
  messages: {
    name: 'Messages',
    nativeScheme: (text) => `sms:?body=${encodeURIComponent(text)}`,
    webFallback: (text) => `sms:?body=${encodeURIComponent(text)}`,
  },
  email: {
    name: 'Email',
    nativeScheme: (text, url, subject) => `mailto:?subject=${encodeURIComponent(subject || 'Check this out!')}&body=${encodeURIComponent(text + (url ? '\n\n' + url : ''))}`,
    webFallback: (text, url, subject) => `mailto:?subject=${encodeURIComponent(subject || 'Check this out!')}&body=${encodeURIComponent(text + (url ? '\n\n' + url : ''))}`,
  },
};

/**
 * Try to open a native URI scheme. Returns true if likely opened.
 */
function tryNativeScheme(uri) {
  return new Promise((resolve) => {
    const start = Date.now();
    let hidden = false;

    const onBlur = () => { hidden = true; };
    const onVisChange = () => {
      if (document.hidden) hidden = true;
    };

    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisChange);

    // Use location.href for scheme links in webview (window.open may be blocked)
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = uri;
    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisChange);

      // If page lost focus or visibility changed, the app likely opened
      if (hidden || Date.now() - start > 1500) {
        resolve(true);
      } else {
        resolve(false);
      }
    }, 800);
  });
}

/**
 * Share to a specific platform using native deep links with fallbacks
 * @param {string} platform - Platform key from PLATFORMS
 * @param {string} text - Share text
 * @param {string} url - URL to share (optional)
 * @param {string} subject - Email subject (optional)
 */
export async function shareToApp(platform, text, url = '', subject = '') {
  const config = PLATFORMS[platform];
  if (!config) {
    return shareViaWebAPI(text, url, subject);
  }

  // For email and messages, always use the scheme directly (they're universal)
  if (platform === 'email' || platform === 'messages') {
    window.location.href = config.nativeScheme(text, url, subject);
    return;
  }

  // In WebView (mobile app), use direct navigation for native schemes
  // The iframe trick doesn't work inside WebView
  const isWebView = typeof window !== 'undefined' && window.isNativeApp;
  
  if (isWebView) {
    // In native WebView, try native scheme directly via location
    // The WebView wrapper will handle the scheme appropriately
    const nativeUri = config.nativeScheme(text, url, subject);
    try {
      window.location.href = nativeUri;
    } catch (e) {
      // Fallback to web URL
      const webUrl = config.webFallback(text, url, subject);
      window.location.href = webUrl;
    }
    return;
  }

  // On regular web, try Web Share API first (most reliable)
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'DuoLyst',
        text: text + (url ? '\n' + url : ''),
      });
      return;
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
  }

  // Fallback to web URL
  const webUrl = config.webFallback(text, url, subject);
  window.open(webUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Use the browser's native Web Share API (system share sheet)
 */
export async function shareViaWebAPI(text, url = '', title = '') {
  if (navigator.share) {
    try {
      await navigator.share({
        title: title || 'DuoLyst',
        text,
        url,
      });
      return true;
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Web Share failed:', e);
      }
      return false;
    }
  }
  return false;
}

/**
 * Get list of available share targets for UI rendering
 */
export function getShareTargets() {
  return [
    { key: 'whatsapp', name: 'WhatsApp', color: 'bg-green-500 hover:bg-green-600' },
    { key: 'twitter', name: 'X', color: 'bg-black hover:bg-gray-800' },
    { key: 'facebook', name: 'Facebook', color: 'bg-blue-600 hover:bg-blue-700' },
    { key: 'telegram', name: 'Telegram', color: 'bg-sky-500 hover:bg-sky-600' },
    { key: 'instagram', name: 'Instagram', color: 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90' },
    { key: 'messages', name: 'Messages', color: 'bg-emerald-500 hover:bg-emerald-600' },
    { key: 'email', name: 'Email', color: 'bg-gray-600 hover:bg-gray-700' },
  ];
}

export { PLATFORMS };
